/**
 * Journal reconciliation after a reload, a crash or a closed tab.
 *
 * Chain state is the authority: a deposit is settled when its commitment
 * record exists, a settlement when its first nullifier PDA exists. Records
 * whose transaction never landed are closed once they provably cannot land.
 * Restored pre-submission intents need an explicit user action to relay.
 */
import {
  decodeSolanaCommitmentRecord,
  getSolanaAccounts,
  solanaPoolAddresses,
} from "@stealthos/sdk";
import type { JoinSplitNote } from "../../joinsplit/types";
import type { SolanaNetworkConfig } from "../../config/solana";
import type { SolanaDepositLog } from "./deposit";
import {
  finalizeSolanaDeposit,
  finalizeSolanaSettlement,
  settlementDeliveries,
} from "./finalize";
import { finalizeSolanaDepositMerge } from "./depositMerge";
import {
  activeSolanaJournalRecords,
  adoptVerifiedSolanaJournalEnvelope,
  getSolanaJournalRecord,
  selectSolanaJournalEnvelopeForSubmission,
  solanaJournalIntentFingerprint,
  updateSolanaJournalRecord,
  type SolanaJournalRecord,
  type SolanaJournalScope,
} from "./journal";
import type { SolanaJournalBackupVariant } from "./journalBackup";
import { SolanaRelayError, type SolanaRelayClient } from "./relay";
import { solanaPoolAccountExists } from "./accounts";
import { depositWindowClosed } from "./transactionWindow";
import {
  isSolanaSignatureExpired,
  getSolanaSignatureStatuses,
  getSolanaSignaturesForAddress,
  type SolanaRpc,
} from "./rpc";

export interface SolanaReconciliationResult {
  /** Output notes owned by this account that became spendable. */
  finalizedNotes: JoinSplitNote[];
  spentCommitments: string[];
  /** Finalized deposits or settlements awaiting durable note delivery. */
  deliveryPending: SolanaJournalRecord[];
  failed: Array<{ id: string; error: string }>;
  stillPending: string[];
  /** Legacy public deposits without an input reservation remain observable. */
  watchOnly?: string[];
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function intentIdentity(record: SolanaJournalRecord): string {
  if (!record.intent) return "missing";
  return (
    record.intent.payload ||
    JSON.stringify(record.intent, (_key, value) =>
      typeof value === "bigint" ? value.toString() : value,
    )
  );
}

const SIGNATURE_HISTORY_PAGE_SIZE = 100;

/** An address mention alone does not establish which transaction created it. */
async function finalizedCreation<T>(
  rpc: SolanaRpc,
  pda: string,
  knownSignature: string | undefined,
  verify: (signature: string) => Promise<T>,
  signal?: AbortSignal,
  expectedSlot?: bigint,
): Promise<{ signature: string; value: T } | null> {
  const examined = new Set<string>();
  const trySignature = async (signature: string) => {
    if (examined.has(signature)) return null;
    examined.add(signature);
    try {
      return { signature, value: await verify(signature) };
    } catch {
      // This may be an unrelated transaction that merely mentions the PDA.
      return null;
    }
  };
  if (knownSignature) {
    const known = await trySignature(knownSignature);
    if (known) return known;
  }

  let before: string | undefined;
  const cursors = new Set<string>();
  while (!signal?.aborted) {
    const page = await getSolanaSignaturesForAddress(rpc, pda, {
      before,
      limit: SIGNATURE_HISTORY_PAGE_SIZE,
      commitment: "finalized",
    });
    for (const candidate of page) {
      if (signal?.aborted) return null;
      if (candidate.err !== null) continue;
      if (
        expectedSlot !== undefined &&
        BigInt(candidate.slot) !== expectedSlot
      ) {
        continue;
      }
      const verified = await trySignature(candidate.signature);
      if (verified) return verified;
    }
    if (page.length < SIGNATURE_HISTORY_PAGE_SIZE) return null;
    const cursor = page[page.length - 1].signature;
    if (cursors.has(cursor)) return null;
    cursors.add(cursor);
    before = cursor;
  }
  return null;
}

export async function reconcileSolanaJournal(params: {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  scope: SolanaJournalScope;
  relay?: SolanaRelayClient;
  backupJournal?: (record: SolanaJournalRecord) => Promise<void>;
  backupVariants?: ReadonlyMap<string, readonly SolanaJournalBackupVariant[]>;
  /** Explicit user action to submit one restored pre-submission intent. */
  resumeRecordId?: string;
  /** Exact authenticated version chosen by the user for a conflicting ID. */
  resumeVariant?: SolanaJournalBackupVariant;
  onLog?: SolanaDepositLog;
  signal?: AbortSignal;
}): Promise<SolanaReconciliationResult> {
  const { rpc, network, scope } = params;
  const onLog = params.onLog ?? (() => {});
  const addresses = solanaPoolAddresses(network.programId);
  const result: SolanaReconciliationResult = {
    finalizedNotes: [],
    spentCommitments: [],
    deliveryPending: [],
    failed: [],
    stillPending: [],
    watchOnly: [],
  };
  const records = await activeSolanaJournalRecords(scope, {
    skipUnreadable: true,
    onUnreadable: (count) =>
      onLog(
        `Skipped ${count} unreadable local Solana recovery record${count === 1 ? "" : "s"}; encrypted bytes were preserved`,
        "warning",
      ),
  });
  // A local device may have a confirmed refusal while another device's
  // backed-up version actually landed. Inspect its variants for chain proof
  // even though the local terminal record is excluded from active records.
  for (const [id, versions] of params.backupVariants ?? []) {
    if (records.some((record) => record.id === id) || versions.length === 0)
      continue;
    const local = await getSolanaJournalRecord(scope, id);
    if (
      local &&
      ["finalized", "delivery_pending", "completed"].includes(local.state)
    )
      continue;
    if (
      local &&
      (local.state === "expired" ||
        (local.state === "failed" && local.failureConfirmed === true)) &&
      versions.every(
        ({ record: version }) =>
          version.state === "prepared" &&
          !version.signature &&
          version.submissionAttempted !== true &&
          solanaJournalIntentFingerprint(version) ===
            solanaJournalIntentFingerprint(local),
      )
    ) {
      // An immutable pre-submission backup is not a new attempt. Preserve the
      // definitive local outcome for this exact version (including createdAt).
      // A different authenticated version must still be checked against chain.
      continue;
    }
    records.push(local ?? versions[versions.length - 1].record);
  }

  const ownNotes = (record: SolanaJournalRecord, outputs: JoinSplitNote[]) =>
    outputs.filter((note) =>
      record.deliveries.some(
        (delivery) =>
          delivery.keepLocal &&
          delivery.commitment.toLowerCase() === note.commitment.toLowerCase(),
      ),
    );

  const completeSettlement = async (
    record: SolanaJournalRecord,
    signature: string,
    verified?: Awaited<ReturnType<typeof finalizeSolanaSettlement>>,
    envelope?: SolanaJournalBackupVariant["envelope"],
  ) => {
    const finalized =
      verified ??
      (await finalizeSolanaSettlement(rpc, network, {
        signature,
        nullifiers: record.sourceNullifiers,
        commitments:
          record.intent?.commitments ??
          record.outputNotes.map((n) => n.commitment),
        outputs: record.outputNotes,
        expectedIntent: record.intent,
        swapSeed: record.swapSeed,
      }));
    if (envelope) {
      await adoptVerifiedSolanaJournalEnvelope(scope, record.id, envelope);
    }
    const updated = await updateSolanaJournalRecord(
      scope,
      record.id,
      {
        state: "finalized",
        signature,
        slot: finalized.slot,
        outputNotes: finalized.outputs,
        deliveries: settlementDeliveries(record, finalized.outputs),
      },
      record,
    );
    result.spentCommitments.push(...record.sourceCommitments);
    result.finalizedNotes.push(...ownNotes(updated, finalized.outputs));
    result.deliveryPending.push(updated);
    onLog(
      `Recovered settlement ${record.id.slice(0, 8)} from chain`,
      "success",
      signature,
    );
  };

  type RecoveryCandidate = {
    record: SolanaJournalRecord;
    envelope?: SolanaJournalBackupVariant["envelope"];
  };
  const candidatesFor = (record: SolanaJournalRecord): RecoveryCandidate[] => {
    const nullifiers = [...record.sourceNullifiers]
      .map((value) => value.toLowerCase())
      .sort()
      .join(",");
    const seen = new Set<string>();
    return [{ record }, ...(params.backupVariants?.get(record.id) ?? [])]
      .filter(
        ({ record: version }) =>
          version.id === record.id &&
          version.network === record.network &&
          version.programId === record.programId &&
          version.account === record.account &&
          [...version.sourceNullifiers]
            .map((value) => value.toLowerCase())
            .sort()
            .join(",") === nullifiers,
      )
      .filter(({ record: version }) => {
        const identity = `${solanaJournalIntentFingerprint(version)}:${version.signature ?? ""}`;
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
      });
  };
  const recoverSpentCandidate = async ({
    record,
    envelope,
  }: RecoveryCandidate): Promise<boolean> => {
    const merge =
      record.kind === "deposit_merge" ? record.deposit?.merge : undefined;
    if (
      record.kind === "deposit_merge" &&
      (!merge || !record.deposit || record.outputNotes.length !== 1)
    )
      return false;
    if (!merge && !record.intent) return false;
    const commitment = merge
      ? record.deposit!.commitment
      : record.intent!.commitments[record.intent!.commitments.length - 1];
    let expectedSlot: bigint | undefined;
    const pda = merge
      ? addresses.commitmentRecord(commitment as `0x${string}`)
      : addresses.nullifier(record.sourceNullifiers[0] as `0x${string}`);
    if (commitment) {
      const { accounts } = await getSolanaAccounts(
        rpc,
        [addresses.commitmentRecord(commitment as `0x${string}`)],
        "finalized",
      );
      if (
        !solanaPoolAccountExists(accounts[0], network.programId, "commitment")
      )
        return false;
      expectedSlot = decodeSolanaCommitmentRecord(accounts[0]!.data).slot;
    }
    if (!merge) {
      const creation = await finalizedCreation(
        rpc,
        pda,
        record.signature,
        (signature) =>
          finalizeSolanaSettlement(rpc, network, {
            signature,
            nullifiers: record.sourceNullifiers,
            commitments: record.intent!.commitments,
            outputs: record.outputNotes,
            expectedIntent: record.intent,
            swapSeed: record.swapSeed,
          }),
        params.signal,
        expectedSlot,
      );
      if (!creation) return false;
      await completeSettlement(
        record,
        creation.signature,
        creation.value,
        envelope,
      );
      return true;
    }
    const creation = await finalizedCreation(
      rpc,
      pda,
      record.signature,
      (signature) =>
        finalizeSolanaDepositMerge({
          rpc,
          network,
          signature,
          prepared: {
            note: record.outputNotes[0],
            sourceNullifier: merge.sourceNullifier as `0x${string}`,
            deadlineSlot: BigInt(merge.deadlineSlot),
            nonce: merge.nonce as `0x${string}`,
          },
          mint: record.deposit!.mint,
          netCredit: BigInt(record.deposit!.amount),
        }),
      params.signal,
      expectedSlot,
    );
    if (!creation) return false;
    if (envelope)
      await adoptVerifiedSolanaJournalEnvelope(scope, record.id, envelope);
    const updated = await updateSolanaJournalRecord(
      scope,
      record.id,
      {
        state: "finalized",
        signature: creation.signature,
        slot: creation.value.slot,
        outputNotes: [creation.value.note],
      },
      record,
    );
    result.spentCommitments.push(...record.sourceCommitments);
    result.finalizedNotes.push(...ownNotes(updated, updated.outputNotes));
    result.deliveryPending.push(updated);
    onLog(
      "Recovered an atomic deposit merge from chain",
      "success",
      creation.signature,
    );
    return true;
  };

  const watchSpentAttempt = async (
    record: SolanaJournalRecord,
  ): Promise<boolean> => {
    if (record.sourceNullifiers.length === 0) return false;
    const { accounts } = await getSolanaAccounts(
      rpc,
      record.sourceNullifiers.map((word) =>
        addresses.nullifier(word as `0x${string}`),
      ),
      "finalized",
    );
    const spent = accounts.map((account) =>
      solanaPoolAccountExists(account, network.programId, "nullifier"),
    );
    if (!spent.some(Boolean)) return false;
    // Any rooted spent input prevents the old proof from ever executing again.
    // Preserve the encrypted outputs for receipt discovery, but release inputs
    // which this attempt did not spend. Never invent a successful output.
    if (record.state !== "watching") {
      await updateSolanaJournalRecord(
        scope,
        record.id,
        {
          state: "watching",
          error:
            "An input was already spent; this attempt cannot execute again. Saved outputs remain under recovery.",
        },
        record,
      );
      onLog(
        "Resolved an obsolete attempt: its input was already spent; remaining notes are available",
        "info",
      );
    }
    result.spentCommitments.push(
      ...record.sourceCommitments.filter((_value, index) => spent[index]),
    );
    result.watchOnly!.push(record.id);
    return true;
  };

  for (const record of records) {
    if (params.signal?.aborted) break;
    try {
      if (record.kind === "deposit_merge") {
        if (
          record.state === "finalized" ||
          record.state === "delivery_pending"
        ) {
          result.spentCommitments.push(...record.sourceCommitments);
          result.finalizedNotes.push(...ownNotes(record, record.outputNotes));
          result.deliveryPending.push(record);
          continue;
        }
        const candidates = candidatesFor(record);
        let recovered = false;
        for (const candidate of candidates) {
          if (await recoverSpentCandidate(candidate)) {
            recovered = true;
            break;
          }
        }
        if (recovered) continue;
        if (await watchSpentAttempt(record)) continue;

        // A finalized bank after every authenticated deadline, with neither a
        // spent nullifier nor a replacement, proves these merges cannot land.
        // Wall-clock age or the newest backup alone cannot establish this.
        if (
          record.sourceNullifiers.length === 1 &&
          candidates.every(
            ({ record: version }) =>
              version.kind === "deposit_merge" &&
              version.deposit?.merge &&
              version.outputNotes.length === 1,
          )
        ) {
          const { slot, accounts } = await getSolanaAccounts(
            rpc,
            [
              addresses.nullifier(record.sourceNullifiers[0] as `0x${string}`),
              ...candidates.map(({ record: version }) =>
                addresses.commitmentRecord(
                  version.deposit!.commitment as `0x${string}`,
                ),
              ),
            ],
            "finalized",
          );
          const expired = (
            await Promise.all(
              candidates.map(
                async ({ record: version }) =>
                  BigInt(slot) > BigInt(version.deposit!.merge!.deadlineSlot) ||
                  (await depositWindowClosed(rpc, network.programId, version)),
              ),
            )
          ).every(Boolean);
          const absent =
            !solanaPoolAccountExists(
              accounts[0],
              network.programId,
              "nullifier",
            ) &&
            accounts
              .slice(1)
              .every(
                (account) =>
                  !solanaPoolAccountExists(
                    account,
                    network.programId,
                    "commitment",
                  ),
              );
          if (expired && absent) {
            await updateSolanaJournalRecord(
              scope,
              record.id,
              {
                state: "expired",
                error:
                  "atomic deposit validity window finalized without settlement",
              },
              record,
            );
            onLog(
              "Expired deposit merge resolved: your original note is available again",
              "info",
            );
            continue;
          }
        }
        if (candidates.length === 1 && record.signature) {
          const [status] = await getSolanaSignatureStatuses(rpc, [
            record.signature,
          ]);
          if (status?.err && status.confirmationStatus === "finalized") {
            await updateSolanaJournalRecord(
              scope,
              record.id,
              {
                state: "failed",
                failureConfirmed: true,
                error: `transaction failed on chain: ${JSON.stringify(status.err)}`,
              },
              record,
            );
            result.failed.push({
              id: record.id,
              error: "atomic deposit merge transaction failed",
            });
            continue;
          }
          if (
            !status &&
            record.lastValidBlockHeight !== undefined &&
            (await isSolanaSignatureExpired(
              rpc,
              record.signature,
              record.lastValidBlockHeight,
            ))
          ) {
            await updateSolanaJournalRecord(
              scope,
              record.id,
              {
                state: "expired",
                error:
                  "blockhash expired before the deposit merge was confirmed",
              },
              record,
            );
            continue;
          }
        }
        result.stillPending.push(record.id);
        continue;
      }

      if (record.kind === "deposit") {
        const deposit = record.deposit;
        const note = record.outputNotes[0];
        if (!deposit || !note) {
          await updateSolanaJournalRecord(
            scope,
            record.id,
            {
              state: "failed",
              failureConfirmed: true,
              error: "deposit record is incomplete",
            },
            record,
          );
          result.failed.push({
            id: record.id,
            error: "deposit record is incomplete",
          });
          continue;
        }
        if (
          record.state === "finalized" ||
          record.state === "delivery_pending"
        ) {
          result.finalizedNotes.push(...ownNotes(record, record.outputNotes));
          result.deliveryPending.push(record);
          continue;
        }
        const recordPda = addresses.commitmentRecord(
          deposit.commitment as `0x${string}`,
        );
        const { accounts } = await getSolanaAccounts(
          rpc,
          [recordPda],
          "finalized",
        );
        if (
          solanaPoolAccountExists(accounts[0], network.programId, "commitment")
        ) {
          const creation = await finalizedCreation(
            rpc,
            recordPda,
            record.signature,
            (signature) =>
              finalizeSolanaDeposit(rpc, network, {
                signature,
                note,
                amount: BigInt(deposit.amount),
              }),
            params.signal,
            decodeSolanaCommitmentRecord(accounts[0]!.data).slot,
          );
          if (!creation) {
            result.stillPending.push(record.id);
            continue;
          }
          const updated = await updateSolanaJournalRecord(
            scope,
            record.id,
            {
              state: "finalized",
              signature: creation.signature,
              slot: creation.value.slot,
              outputNotes: [creation.value.note],
            },
            record,
          );
          result.finalizedNotes.push(
            ...ownNotes(updated, [creation.value.note]),
          );
          result.deliveryPending.push(updated);
          onLog(
            "Recovered a deposit from chain",
            "success",
            creation.signature,
          );
          continue;
        }
        const depositCandidates = candidatesFor(record);
        if (
          depositCandidates.every(({ record: version }) =>
            Boolean(version.deposit?.transactionWindow),
          ) &&
          (
            await Promise.all(
              depositCandidates.map(({ record: version }) =>
                depositWindowClosed(rpc, network.programId, version),
              ),
            )
          ).every(Boolean)
        ) {
          await updateSolanaJournalRecord(
            scope,
            record.id,
            {
              state: "expired",
              error:
                "deposit signing window closed without a finalized deposit",
            },
            record,
          );
          onLog(
            "Expired deposit resolved: no funds were deposited; you can retry",
            "info",
          );
          continue;
        }
        if (
          record.signature &&
          depositCandidates.every(
            ({ record: version }) =>
              !version.signature || version.signature === record.signature,
          )
        ) {
          const [status] = await getSolanaSignatureStatuses(rpc, [
            record.signature,
          ]);
          if (status?.err && status.confirmationStatus === "finalized") {
            await updateSolanaJournalRecord(
              scope,
              record.id,
              {
                state: "failed",
                failureConfirmed: true,
                error: `transaction failed on chain: ${JSON.stringify(status.err)}`,
              },
              record,
            );
            result.failed.push({
              id: record.id,
              error: "deposit transaction failed",
            });
            continue;
          }
          if (
            !status &&
            record.lastValidBlockHeight !== undefined &&
            (await isSolanaSignatureExpired(
              rpc,
              record.signature,
              record.lastValidBlockHeight,
            ))
          ) {
            await updateSolanaJournalRecord(
              scope,
              record.id,
              {
                state: "expired",
                error: "blockhash expired before the deposit was confirmed",
              },
              record,
            );
            continue;
          }
          result.stillPending.push(record.id);
          continue;
        }
        if (
          !record.deposit.transactionWindow &&
          record.sourceNullifiers.length === 0 &&
          record.sourceCommitments.length === 0
        ) {
          // Old public-deposit backups reserve no private inputs. Keep their
          // ciphertext and monitor receipts without freezing unrelated funds.
          if (record.state !== "watching") {
            await updateSolanaJournalRecord(
              scope,
              record.id,
              { state: "watching" },
              record,
            );
            onLog(
              "An older unsigned deposit backup is being watched for confirmation; it does not reserve your private balance",
              "info",
            );
          }
          result.watchOnly!.push(record.id);
          continue;
        }
        result.stillPending.push(record.id);
        continue;
      }

      // Relayed settlements.
      if (record.state === "finalized" || record.state === "delivery_pending") {
        result.deliveryPending.push(record);
        continue;
      }
      const firstNullifier = record.sourceNullifiers[0];
      if (!firstNullifier || !record.intent) {
        await updateSolanaJournalRecord(
          scope,
          record.id,
          {
            state: "failed",
            failureConfirmed: true,
            error: "settlement record is incomplete",
          },
          record,
        );
        result.failed.push({
          id: record.id,
          error: "settlement record is incomplete",
        });
        continue;
      }
      const { accounts } = await getSolanaAccounts(
        rpc,
        record.sourceNullifiers.map((word) =>
          addresses.nullifier(word as `0x${string}`),
        ),
        "finalized",
      );
      if (
        accounts.some((account) =>
          solanaPoolAccountExists(account, network.programId, "nullifier"),
        )
      ) {
        let matched = false;
        for (const candidate of candidatesFor(record)) {
          if (await recoverSpentCandidate(candidate)) {
            matched = true;
            break;
          }
        }

        if (!matched) {
          await watchSpentAttempt(record);
        }
        continue;
      }
      if (record.signature) {
        const [status] = await getSolanaSignatureStatuses(rpc, [
          record.signature,
        ]);
        if (status?.err && status.confirmationStatus === "finalized") {
          await updateSolanaJournalRecord(
            scope,
            record.id,
            {
              state: "failed",
              failureConfirmed: true,
              error: `settlement failed on chain: ${JSON.stringify(status.err)}`,
            },
            record,
          );
          result.failed.push({
            id: record.id,
            error: "settlement transaction failed",
          });
          continue;
        }
        if (status?.confirmationStatus === "finalized") {
          await completeSettlement(record, record.signature);
          continue;
        }
      }
      if (params.relay?.recover && params.resumeRecordId !== record.id) {
        let closed = true;
        let settled = false;
        let reason: string | undefined;
        for (const candidate of candidatesFor(record)) {
          if (!candidate.record.intent) {
            closed = false;
            continue;
          }
          const outcome = await params.relay.recover(candidate.record.intent, {
            signal: params.signal,
          });
          if (outcome.status === "settled") {
            await completeSettlement(
              candidate.record,
              outcome.signature!,
              undefined,
              candidate.envelope,
            );
            settled = true;
            break;
          }
          if (outcome.status !== "closed") {
            closed = false;
            reason = outcome.reason;
          }
        }
        if (settled) continue;
        if (closed) {
          await updateSolanaJournalRecord(
            scope,
            record.id,
            {
              state: "expired",
              error: "Unused or expired relayer attempt safely stopped",
            },
            record,
          );
          onLog(
            "Recovery resolved an unused or expired transaction: reserved notes are available again",
            "success",
          );
        } else {
          if (reason)
            await updateSolanaJournalRecord(
              scope,
              record.id,
              { error: reason },
              record,
            );
          result.stillPending.push(record.id);
        }
        continue;
      }
      const versions = params.backupVariants?.get(record.id) ?? [];
      const conflictingIntents =
        new Set([
          intentIdentity(record),
          ...versions.map((candidate) => intentIdentity(candidate.record)),
        ]).size > 1;
      const selectedVariant =
        params.resumeRecordId === record.id && params.resumeVariant
          ? versions.find(
              (candidate) =>
                candidate.backupId === params.resumeVariant?.backupId &&
                candidate.record.id === record.id,
            )
          : undefined;
      if (
        (record.state === "failed" &&
          record.failureConfirmed === true &&
          !selectedVariant) ||
        (conflictingIntents && !selectedVariant)
      ) {
        // Different payments can share a nullifier set across devices. Chain
        // discovery may resolve which landed; otherwise a user must choose
        // one exact backed-up intent before any new relayer call.
        result.stillPending.push(record.id);
        continue;
      }
      if (!params.relay) {
        result.stillPending.push(record.id);
        continue;
      }
      if (
        params.resumeRecordId !== record.id &&
        (record.restoredFromBackup === true ||
          (record.submissionAttempted !== true && !record.signature))
      ) {
        // A remote prepared backup does not prove the browser ever submitted
        // this intent. Discover chain state, but do not initiate a payment.
        result.stillPending.push(record.id);
        continue;
      }
      const submissionRecord = selectedVariant
        ? await selectSolanaJournalEnvelopeForSubmission(
            scope,
            record.id,
            selectedVariant.envelope,
          )
        : record;
      // A crash or failed backup acknowledgement may leave an intent prepared.
      // Never let automatic replay bypass the same durable-backup gate as a send.
      try {
        if (!params.backupJournal) {
          throw new Error(
            "encrypted Solana recovery backup is required before relay replay",
          );
        }
        await params.backupJournal(submissionRecord);
      } catch (error) {
        await updateSolanaJournalRecord(
          scope,
          record.id,
          { error: message(error) },
          submissionRecord,
        );
        result.stillPending.push(record.id);
        continue;
      }
      if (submissionRecord.submissionAttempted !== true) {
        await updateSolanaJournalRecord(
          scope,
          record.id,
          {
            submissionAttempted: true,
          },
          submissionRecord,
        );
      }
      // Nothing on chain yet: the relayer's journal decides whether this is a
      // replay, an attempt in flight, or a fresh submission.
      try {
        const response = await params.relay.settle(submissionRecord.intent!, {
          idempotencyKey: record.id,
          signal: params.signal,
        });
        await updateSolanaJournalRecord(
          scope,
          record.id,
          {
            state: "broadcast",
            signature: response.settleSignature,
          },
          submissionRecord,
        );
        const [status] = await getSolanaSignatureStatuses(rpc, [
          response.settleSignature,
        ]);
        if (status?.confirmationStatus === "finalized" && status.err === null) {
          await completeSettlement(submissionRecord, response.settleSignature);
        } else {
          result.stillPending.push(record.id);
        }
      } catch (error) {
        const refused =
          error instanceof SolanaRelayError &&
          (error.submissionNotAttempted || error.settlementNotSubmitted);
        await updateSolanaJournalRecord(
          scope,
          record.id,
          {
            ...(refused
              ? { state: "failed" as const, failureConfirmed: true }
              : {}),
            error: message(error),
          },
          submissionRecord,
        );
        if (refused)
          result.failed.push({ id: record.id, error: message(error) });
        else result.stillPending.push(record.id);
      }
    } catch (error) {
      onLog(
        `Journal record ${record.id.slice(0, 8)} could not be reconciled: ${message(error)}`,
        "warning",
      );
      result.stillPending.push(record.id);
    }
  }
  return result;
}
